import React, { useEffect, useMemo, useState } from 'react';
import {
  Callout, ConfirmDialog, EmptyState, FormRow, NumberField, SearchInput, Tabs, Toolbar,
  formatThaiDate, ghostBtnCls, inputCls, primaryBtnCls, secondaryBtnCls, textareaCls, useToast,
} from '../components';
import { Icon } from '../components/base/Icon';

/**
 * MemberScreen — สมาชิกและคะแนน · ปรับคะแนนด้วยมือ (FR-ADM-04)
 *
 * เจ้าหน้าที่ใช้หน้านี้ตอบลูกค้าที่ถามว่า "คะแนนหายไปไหน" และชดเชยคะแนนเมื่อเกิดปัญหา
 * จึงต้องเห็นที่มาของคะแนนทุกแถว และรู้ผลของการปรับ**ก่อน**กดยืนยัน
 *
 *   1. **ประวัติคะแนนบอกที่มาทุกแถว** — คำขอสะสมคะแนน · แลกของรางวัล · ปรับโดยเจ้าหน้าที่ · ย้อนรายการ · หมดอายุ
 *   2. **หน้าต่างปรับคะแนนแสดงยอดก่อน → หลัง และเตือนเมื่อระดับสมาชิกจะเปลี่ยน**
 *      การปรับยอดใช้จ่ายสะสมทำให้ระดับเปลี่ยนทันที — เจ้าหน้าที่ต้องเห็นก่อน ไม่ใช่รู้ทีหลังจากลูกค้าโทรมา
 *
 * ⚠️ "คะแนนสะสมระดับ" ของ backend (tier points) แสดงเป็น **ยอดใช้จ่ายสะสม (บาท)**
 *    เพราะคือยอดซื้อที่ใช้เลื่อนระดับ ไม่ใช่คะแนนที่ลูกค้าใช้ได้ — คำว่าคะแนนทำให้สับสนกับคะแนนแลกของ
 *    ถูกต้องตราบที่กติกาคะแนนตั้ง tier_multiplier = 1 (ยอด 1 บาท = 1) ถ้าเปลี่ยนต้องกลับมาแก้หน่วย
 *   3. **เหตุผลบังคับ และแยกจากข้อความที่ลูกค้าเห็น** — เหตุผลเป็นข้อมูลภายใน ลูกค้าเห็นแค่ข้อความถึงสมาชิก
 *   4. **กดยืนยันซ้ำไม่ปรับซ้ำ** — รหัสคำขอถูกสร้างตอนเปิดหน้าต่าง และใช้ค่าเดิมเมื่อกดซ้ำหรือลองใหม่หลังเน็ตหลุด
 */

/* ---------------------------------------------------------------- ชนิดข้อมูล */

export type PointKind = 'REWARD' | 'TIER';
export type PointSource = 'CLAIM' | 'REDEMPTION' | 'ADJUSTMENT' | 'ROLLBACK' | 'EXPIRY' | 'OTHER';
export type AdjustmentReason =
  | 'SERVICE_COMPENSATION' | 'SYSTEM_CORRECTION' | 'SPECIAL_CAMPAIGN' | 'IMPROPER_POINTS' | 'OTHER';

export const REASON_LABEL: Record<AdjustmentReason, string> = {
  SERVICE_COMPENSATION: 'ชดเชยปัญหาการบริการ',
  SYSTEM_CORRECTION: 'แก้ไขความผิดพลาดของระบบ',
  SPECIAL_CAMPAIGN: 'แคมเปญพิเศษ',
  IMPROPER_POINTS: 'ยกเลิกคะแนนที่ได้โดยมิชอบ',
  OTHER: 'อื่น ๆ',
};

const SOURCE_LABEL: Record<PointSource, { text: string; icon: string }> = {
  CLAIM: { text: 'คำขอสะสมคะแนน', icon: 'receipt_long' },
  REDEMPTION: { text: 'แลกของรางวัล', icon: 'redeem' },
  ADJUSTMENT: { text: 'ปรับโดยเจ้าหน้าที่', icon: 'edit_note' },
  ROLLBACK: { text: 'ย้อนรายการ', icon: 'history' },
  EXPIRY: { text: 'หมดอายุ', icon: 'event_busy' },
  OTHER: { text: 'อื่น ๆ', icon: 'info' },
};

const KIND_LABEL: Record<PointKind, string> = { REWARD: 'คะแนนแลกของ', TIER: 'ยอดใช้จ่ายสะสม' };
const KIND_UNIT: Record<PointKind, string> = { REWARD: 'คะแนน', TIER: 'บาท' };

export interface MemberSummary {
  id: string;
  name: string;
  tierName: string | null;
  rewardPoints: number;
}

export interface MemberTierStep {
  id: string;
  name: string;
  /** ยอดใช้จ่ายสะสมขั้นต่ำของระดับนี้ */
  startPoint: number;
}

export interface MemberDetail {
  id: string;
  name: string;
  rewardPoints: number;
  tierPoints: number;
  tierName: string | null;
  nextTier: { name: string; startPoint: number } | null;
  /** ก้อนคะแนนแลกของที่จะหมดอายุก่อน */
  expiringReward: { points: number; at: string } | null;
}

export interface PointHistoryItem {
  id: string;
  kind: PointKind;
  source: PointSource;
  amount: number;
  at: string;
  /** วันหมดอายุของก้อนที่แถวนี้อยู่ */
  expiresAt: string;
  cancelled: boolean;
  referenceText: string | null;
  byName: string | null;
  adjustment: { reasonCode: AdjustmentReason; reason: string; customerNote: string } | null;
}

export interface AdjustmentItem {
  id: string;
  status: 'APPLIED' | 'CANCELLED';
  rewardAmount: number;
  tierAmount: number;
  reasonCode: AdjustmentReason;
  reason: string;
  customerNote: string;
  reference: string;
  expiresAt: string | null;
  byName: string;
  at: string;
  cancelledByName: string | null;
  cancelledAt: string | null;
  cancelReason: string;
}

export interface AdjustmentInput {
  /** บวก = เพิ่ม · ลบ = หัก */
  rewardAmount: number;
  tierAmount: number;
  reasonCode: AdjustmentReason;
  reason: string;
  customerNote: string;
  reference: string;
  /** วันสุดท้ายที่ใช้คะแนนที่เพิ่มได้ YYYY-MM-DD · null = ตามกติกาคะแนน */
  expiresOn: string | null;
  /** สร้างตอนเปิดหน้าต่าง — กดซ้ำหรือลองใหม่ใช้ค่าเดิม backend จึงไม่ปรับซ้ำ */
  requestId: string;
}

export interface AdjustmentOutcome {
  isNew: boolean;
  rewardBefore: number;
  rewardAfter: number;
  tierPointsBefore: number;
  tierPointsAfter: number;
  tierBefore: string | null;
  tierAfter: string | null;
  tierChanged: boolean;
  /** เฉพาะตอนยกเลิก — คะแนนที่คืนเข้าก้อนที่หมดอายุแล้ว ใช้ไม่ได้ */
  expiredReward: number;
  expiredTier: number;
}

export interface MemberScreenProps {
  initialState?: 'ready' | 'loading' | 'error';
  members: MemberSummary[];
  /** โหลดมาไม่ครบทุกคน — บอกผู้ใช้ให้ค้นด้วยชื่อ */
  membersTruncated?: boolean;
  tiers: MemberTierStep[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  detail: MemberDetail | null;
  detailState: 'idle' | 'loading' | 'ready' | 'error';
  history: PointHistoryItem[];
  historyHasMore: boolean;
  historyLoading: boolean;
  onLoadMoreHistory: () => void;
  adjustments: AdjustmentItem[];
  /** throw = ไม่สำเร็จ ข้อความของ error แสดงในหน้าต่างให้แก้แล้วลองใหม่ */
  onAdjust: (input: AdjustmentInput) => Promise<AdjustmentOutcome>;
  onCancel: (adjustmentId: string, reason: string) => Promise<AdjustmentOutcome>;
}

/* ------------------------------------------------------------------ ตัวช่วย */

const REASON_MIN = 10;
const NOTE_MAX = 200;
const REFERENCE_MAX = 100;
const DEFAULT_CUSTOMER_NOTE = 'ปรับคะแนนโดยเจ้าหน้าที่';

const pts = (n: number) => n.toLocaleString('th-TH', { maximumFractionDigits: 2 });
const signed = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${pts(Math.abs(n))}`;

/** วันเวลาแบบไทย พ.ศ. ตามเวลาไทย — ไม่ขึ้นกับ timezone ของเครื่อง */
const bkk = (iso: string) => new Date(new Date(iso).getTime() + 7 * 3_600_000).toISOString();
const fmtDate = (iso: string | null) => (iso ? formatThaiDate(bkk(iso).slice(0, 10)) : '—');
const fmtDateTime = (iso: string) => `${fmtDate(iso)} ${bkk(iso).slice(11, 16)} น.`;
const todayBkk = () => bkk(new Date().toISOString()).slice(0, 10);

/** UUID สำหรับกันกดซ้ำ — crypto.randomUUID ไม่มีในเบราว์เซอร์เก่าและบน http */
const newRequestId = (): string => {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (c) c.getRandomValues(b); else for (let i = 0; i < 16; i += 1) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
};

const tierFor = (tiers: MemberTierStep[], points: number): MemberTierStep | null =>
  [...tiers].sort((a, b) => a.startPoint - b.startPoint).filter((t) => t.startPoint <= points).pop() ?? null;

const amountColor = (n: number) => (n > 0 ? 'var(--success-text)' : n < 0 ? 'var(--error-text)' : 'var(--ink-700)');

/* -------------------------------------------------------------- หน้าต่างปรับ */

type Direction = 'add' | 'deduct';
type FieldKey = 'amount' | 'reward' | 'tier' | 'reasonCode' | 'reason' | 'customerNote' | 'reference' | 'expiresOn';

interface AdjustDraft {
  direction: Direction;
  reward: number | '';
  tier: number | '';
  reasonCode: AdjustmentReason | '';
  reason: string;
  customerNote: string;
  reference: string;
  expiresOn: string;
  requestId: string;
}

const emptyDraft = (): AdjustDraft => ({
  direction: 'add', reward: '', tier: '', reasonCode: '', reason: '', customerNote: '', reference: '', expiresOn: '',
  requestId: newRequestId(),
});

function draftProblems(d: AdjustDraft, m: MemberDetail): Partial<Record<FieldKey, string>> {
  const out: Partial<Record<FieldKey, string>> = {};
  const reward = d.reward === '' ? 0 : d.reward;
  const tier = d.tier === '' ? 0 : d.tier;
  if (reward <= 0 && tier <= 0) out.amount = 'กรอกคะแนนแลกของหรือยอดใช้จ่ายสะสมที่จะปรับอย่างน้อยหนึ่งช่อง';
  if (reward < 0 || tier < 0) out.amount = 'กรอกเป็นจำนวนบวก — เลือกเพิ่มหรือหักด้านบน';
  if (d.direction === 'deduct' && reward > m.rewardPoints) out.reward = `หักได้ไม่เกินคงเหลือ ${pts(m.rewardPoints)} คะแนน`;
  if (d.direction === 'deduct' && tier > m.tierPoints) out.tier = `หักได้ไม่เกินยอดสะสม ${pts(m.tierPoints)} บาท`;
  if (!d.reasonCode) out.reasonCode = 'เลือกหมวดเหตุผล';
  if (d.reason.trim().length < REASON_MIN) out.reason = `เหตุผลต้องมีอย่างน้อย ${REASON_MIN} ตัวอักษร (ตอนนี้ ${d.reason.trim().length})`;
  if (d.customerNote.trim().length > NOTE_MAX) out.customerNote = `ไม่เกิน ${NOTE_MAX} ตัวอักษร`;
  if (d.reference.trim().length > REFERENCE_MAX) out.reference = `ไม่เกิน ${REFERENCE_MAX} ตัวอักษร`;
  if (d.direction === 'add' && d.expiresOn && d.expiresOn < todayBkk()) out.expiresOn = 'วันหมดอายุต้องไม่ก่อนวันนี้';
  return out;
}

const FIELD_ORDER: FieldKey[] = ['amount', 'reward', 'tier', 'reasonCode', 'reason', 'customerNote', 'reference', 'expiresOn'];
const FIELD_ID: Record<FieldKey, string> = {
  amount: 'adj-reward', reward: 'adj-reward', tier: 'adj-tier', reasonCode: 'adj-reason-code', reason: 'adj-reason',
  customerNote: 'adj-note', reference: 'adj-reference', expiresOn: 'adj-expires',
};

const AdjustDialog: React.FC<{
  member: MemberDetail;
  tiers: MemberTierStep[];
  onClose: () => void;
  onSubmit: (input: AdjustmentInput) => Promise<void>;
}> = ({ member, tiers, onClose, onSubmit }) => {
  const [d, setD] = useState<AdjustDraft>(emptyDraft);
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const set = (next: Partial<AdjustDraft>) => { setD((x) => ({ ...x, ...next })); setServerError(null); };

  const problems = draftProblems(d, member);
  const err = (k: FieldKey) => (showErrors ? problems[k] : undefined);
  const sign = d.direction === 'add' ? 1 : -1;
  const reward = (d.reward === '' ? 0 : d.reward) * sign;
  const tier = (d.tier === '' ? 0 : d.tier) * sign;
  const rewardAfter = member.rewardPoints + reward;
  const tierPointsAfter = member.tierPoints + tier;
  const tierAfter = tierFor(tiers, tierPointsAfter)?.name ?? null;
  const tierChanges = tier !== 0 && tierAfter !== member.tierName;

  const submit = async () => {
    const keys = FIELD_ORDER.filter((k) => problems[k]);
    if (keys.length) {
      setShowErrors(true);
      window.setTimeout(() => document.getElementById(FIELD_ID[keys[0]!])?.focus(), 0);
      return;
    }
    setBusy(true);
    try {
      await onSubmit({
        rewardAmount: reward, tierAmount: tier, reasonCode: d.reasonCode as AdjustmentReason,
        reason: d.reason.trim(), customerNote: d.customerNote.trim(), reference: d.reference.trim(),
        expiresOn: d.direction === 'add' && d.expiresOn ? d.expiresOn : null, requestId: d.requestId,
      });
    } catch (e) {
      /* รหัสคำขอเดิมยังอยู่ — กดยืนยันอีกครั้งแล้ว backend จะไม่ปรับซ้ำถ้ารอบก่อนสำเร็จไปแล้วจริง */
      setServerError(e instanceof Error ? e.message : 'ปรับคะแนนไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`ปรับคะแนนของ ${member.name}`}
      className="fixed inset-0 z-[var(--z-drawer)] flex items-start justify-center overflow-y-auto p-[var(--space-6)]"
      style={{ background: 'var(--scrim)' }}
    >
      <div
        className="flex w-full max-w-[760px] flex-col gap-[var(--space-4)] rounded-[var(--radius-lg)] bg-[var(--surface-0)] p-[var(--space-5)]"
        style={{ boxShadow: 'var(--elevation-modal)' }}
      >
        <header className="flex items-start justify-between gap-[var(--space-3)]">
          <div className="flex flex-col gap-[var(--space-05)]">
            <h2 className="m-0 text-[length:var(--text-page-title)] font-[var(--weight-semibold)] text-[var(--ink-900)]">ปรับคะแนน</h2>
            <p className="m-0 text-[length:var(--text-form)] text-[var(--ink-700)]">
              {member.name} · คะแนนแลกของ {pts(member.rewardPoints)} คะแนน · ยอดใช้จ่ายสะสม {pts(member.tierPoints)} บาท
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="ปิด"
            className="inline-flex min-h-[var(--hit-control)] min-w-[var(--hit-control)] items-center justify-center rounded-[var(--radius-sm)] text-[var(--ink-700)] outline-none hover:bg-[var(--surface-100)] focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
          >
            <Icon name="close" size={20} />
          </button>
        </header>

        <FormRow label="ต้องการ" required>
          <span className="flex flex-wrap gap-[var(--space-2)]" role="radiogroup" aria-label="เพิ่มหรือหักคะแนน">
            {([['add', 'เพิ่มคะแนน', 'add_circle'], ['deduct', 'หักคะแนน', 'cancel']] as Array<[Direction, string, string]>).map(([k, text, icon]) => (
              <label
                key={k}
                className={`flex cursor-pointer items-center gap-[var(--space-15)] rounded-[var(--radius-pill)] border px-[var(--space-3)] py-[var(--space-1)] text-[length:var(--text-form)] ${
                  d.direction === k ? 'border-[var(--blue-700)] bg-[var(--blue-100)] text-[var(--blue-900)]' : 'border-[var(--line-300)] text-[var(--ink-700)]'
                }`}
              >
                <input
                  type="radio"
                  name="adj-direction"
                  checked={d.direction === k}
                  onChange={() => set({ direction: k })}
                  className="h-4 w-4 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                />
                <Icon name={icon} size={18} />
                {text}
              </label>
            ))}
          </span>
        </FormRow>

        <div className="grid grid-cols-2 gap-[var(--space-3)]">
          <FormRow
            label={KIND_LABEL.REWARD}
            htmlFor="adj-reward"
            help="ใช้แลกของรางวัล — เว้นว่างถ้าไม่ปรับ"
            error={err('reward') ?? err('amount')}
          >
            <NumberField id="adj-reward" value={d.reward} onChange={(v) => set({ reward: v })} unit="คะแนน" min={0} width={200} ariaLabel={KIND_LABEL.REWARD} />
          </FormRow>
          <FormRow
            label={KIND_LABEL.TIER}
            htmlFor="adj-tier"
            help="ใช้คำนวณระดับสมาชิก — ปรับแล้วระดับอาจเปลี่ยนทันที"
            error={err('tier')}
          >
            <NumberField id="adj-tier" value={d.tier} onChange={(v) => set({ tier: v })} unit="บาท" min={0} width={200} ariaLabel={KIND_LABEL.TIER} />
          </FormRow>
        </div>

        <div className="grid grid-cols-2 gap-[var(--space-3)]">
          <FormRow label="หมวดเหตุผล" required htmlFor="adj-reason-code" error={err('reasonCode')}>
            <select
              id="adj-reason-code"
              value={d.reasonCode}
              onChange={(e) => set({ reasonCode: e.target.value as AdjustmentReason })}
              className={`${inputCls} w-full`}
            >
              <option value="">— เลือกหมวดเหตุผล —</option>
              {(Object.keys(REASON_LABEL) as AdjustmentReason[]).map((r) => <option key={r} value={r}>{REASON_LABEL[r]}</option>)}
            </select>
          </FormRow>
          <FormRow label="เลขอ้างอิง" htmlFor="adj-reference" help="เลขคำสั่งซื้อ · เลขคำขอ · เลขเรื่องร้องเรียน" error={err('reference')}>
            <input
              id="adj-reference"
              type="text"
              maxLength={REFERENCE_MAX}
              value={d.reference}
              onChange={(e) => set({ reference: e.target.value })}
              className={`${inputCls} w-full`}
            />
          </FormRow>
        </div>

        <FormRow
          label="เหตุผล"
          required
          htmlFor="adj-reason"
          help={`ข้อมูลภายใน ลูกค้าไม่เห็น — เขียนให้คนอื่นอ่านย้อนหลังแล้วเข้าใจ อย่างน้อย ${REASON_MIN} ตัวอักษร`}
          error={err('reason')}
        >
          <textarea
            id="adj-reason"
            rows={3}
            value={d.reason}
            onChange={(e) => set({ reason: e.target.value })}
            className={`${textareaCls} w-full`}
            placeholder="เช่น ลูกค้าได้รับสินค้าล่าช้า 5 วัน ตามเรื่องร้องเรียนเลขที่ ..."
          />
        </FormRow>

        <FormRow
          label="ข้อความถึงสมาชิก"
          htmlFor="adj-note"
          help={`แสดงในประวัติคะแนนของลูกค้า — เว้นว่างจะแสดงว่า "${DEFAULT_CUSTOMER_NOTE}"`}
          error={err('customerNote')}
        >
          <input
            id="adj-note"
            type="text"
            maxLength={NOTE_MAX}
            value={d.customerNote}
            onChange={(e) => set({ customerNote: e.target.value })}
            className={`${inputCls} w-full`}
            placeholder={DEFAULT_CUSTOMER_NOTE}
          />
        </FormRow>

        {d.direction === 'add' && (
          <FormRow
            label="วันหมดอายุของที่เพิ่ม"
            htmlFor="adj-expires"
            help="ใช้กับทั้งคะแนนแลกของและยอดใช้จ่ายสะสมที่เพิ่มในรายการนี้ — เว้นว่าง = ตามอายุในกติกาคะแนน นับจากวันนี้"
            error={err('expiresOn')}
          >
            <input
              id="adj-expires"
              type="date"
              min={todayBkk()}
              value={d.expiresOn}
              onChange={(e) => set({ expiresOn: e.target.value })}
              className={`${inputCls} bo-num`}
            />
          </FormRow>
        )}

        {/* ผลของการปรับ — ให้เห็นก่อนกดยืนยัน */}
        <section className="flex flex-col gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-100)] p-[var(--space-3)]">
          <h3 className="m-0 text-[length:var(--text-form)] font-[var(--weight-semibold)] text-[var(--ink-900)]">หลังปรับ</h3>
          <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-[var(--space-4)] gap-y-[var(--space-1)] text-[length:var(--text-form)]">
            <dt className="text-[var(--ink-700)]">{KIND_LABEL.REWARD}</dt>
            <dd className="m-0 bo-num" style={{ textAlign: 'left' }}>
              {pts(member.rewardPoints)} → <strong style={{ color: reward ? amountColor(reward) : undefined }}>{pts(rewardAfter)}</strong> คะแนน
              {reward !== 0 && <span className="text-[var(--ink-700)]"> ({signed(reward)})</span>}
            </dd>
            <dt className="text-[var(--ink-700)]">{KIND_LABEL.TIER}</dt>
            <dd className="m-0 bo-num" style={{ textAlign: 'left' }}>
              {pts(member.tierPoints)} → <strong style={{ color: tier ? amountColor(tier) : undefined }}>{pts(tierPointsAfter)}</strong> บาท
              {tier !== 0 && <span className="text-[var(--ink-700)]"> ({signed(tier)})</span>}
            </dd>
            <dt className="text-[var(--ink-700)]">ระดับสมาชิก</dt>
            <dd className="m-0">{member.tierName ?? 'ยังไม่มีระดับ'} → <strong>{tierAfter ?? 'ยังไม่มีระดับ'}</strong></dd>
          </dl>
          {tierChanges && (
            <Callout tone="warning" title="ระดับสมาชิกจะเปลี่ยนทันทีหลังยืนยัน">
              จาก {member.tierName ?? 'ยังไม่มีระดับ'} เป็น {tierAfter ?? 'ยังไม่มีระดับ'} — สิทธิประโยชน์ของลูกค้าจะเปลี่ยนตาม
            </Callout>
          )}
        </section>

        {showErrors && Object.keys(problems).length > 0 && (
          <Callout tone="error" title={`ยังปรับคะแนนไม่ได้ — แก้อีก ${Object.keys(problems).length.toLocaleString('th-TH')} จุด`}>
            <ul className="m-0 pl-[var(--space-4)]">
              {FIELD_ORDER.filter((k) => problems[k]).map((k) => <li key={k}>{problems[k]}</li>)}
            </ul>
          </Callout>
        )}
        {serverError && (
          <Callout tone="error" title="ปรับคะแนนไม่สำเร็จ">{serverError}</Callout>
        )}

        <div className="flex flex-wrap items-center gap-[var(--space-2)] border-t border-[var(--line-200)] pt-[var(--space-3)]">
          <button type="button" onClick={() => { void submit(); }} disabled={busy} className={primaryBtnCls}>
            <Icon name="check_circle" size={17} />
            {busy ? 'กำลังปรับคะแนน…' : 'ยืนยันปรับคะแนน'}
          </button>
          <button type="button" onClick={onClose} disabled={busy} className={secondaryBtnCls}>ยกเลิก</button>
          <span className="flex-1" />
          <span className="text-[length:var(--text-table-sm)] text-[var(--ink-700)]">มีผลทันทีหลังยืนยัน — ยกเลิกภายหลังได้พร้อมเหตุผล</span>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------- หน้าจอ */

export const MemberScreen: React.FC<MemberScreenProps> = ({
  initialState = 'ready', members, membersTruncated = false, tiers, selectedId, onSelect, detail, detailState,
  history, historyHasMore, historyLoading, onLoadMoreHistory, adjustments,
  onAdjust, onCancel,
}) => {
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<'history' | 'adjustments'>('history');
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<AdjustmentItem | null>(null);
  const [cancelBusy, setCancelBusy] = useState(false);

  /* เปลี่ยนสมาชิกแล้วหน้าต่างของคนเดิมต้องไม่ค้าง */
  useEffect(() => { setAdjustOpen(false); setCancelTarget(null); }, [selectedId]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? members.filter((m) => m.name.toLowerCase().includes(q)) : members;
  }, [members, query]);

  const outcomeText = (o: AdjustmentOutcome) => {
    const parts: string[] = [];
    if (o.rewardBefore !== o.rewardAfter) parts.push(`${KIND_LABEL.REWARD} ${pts(o.rewardBefore)} → ${pts(o.rewardAfter)} คะแนน`);
    if (o.tierPointsBefore !== o.tierPointsAfter) parts.push(`${KIND_LABEL.TIER} ${pts(o.tierPointsBefore)} → ${pts(o.tierPointsAfter)} บาท`);
    if (o.tierChanged) parts.push(`ระดับ ${o.tierBefore ?? 'ไม่มี'} → ${o.tierAfter ?? 'ไม่มี'}`);
    return parts.join(' · ');
  };

  const submitAdjust = async (input: AdjustmentInput) => {
    const o = await onAdjust(input);
    setAdjustOpen(false);
    setTab('adjustments');
    toast.show(o.isNew
      ? { tone: 'success', message: `ปรับคะแนนแล้ว — ${outcomeText(o)}` }
      : { tone: 'info', message: 'รายการนี้ถูกบันทึกไปแล้วก่อนหน้า — ไม่ได้ปรับซ้ำ' });
  };

  const confirmCancel = async (reason?: string) => {
    if (!cancelTarget) return;
    setCancelBusy(true);
    try {
      const o = await onCancel(cancelTarget.id, (reason ?? '').trim());
      setCancelTarget(null);
      const expired = o.expiredReward + o.expiredTier;
      toast.show({
        tone: expired > 0 ? 'warning' : 'success',
        message: `ยกเลิกการปรับคะแนนแล้ว — ${outcomeText(o)}${expired > 0 ? ` · ${pts(expired)} คะแนนที่คืนให้อยู่ในก้อนที่หมดอายุแล้ว จึงใช้ไม่ได้` : ''}`,
      });
    } catch (e) {
      toast.show({ tone: 'error', message: e instanceof Error ? e.message : 'ยกเลิกไม่สำเร็จ' });
    } finally {
      setCancelBusy(false);
    }
  };

  if (initialState === 'error') {
    return (
      <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-100)] font-[family-name:var(--font-thai)]">
        <Toolbar title="สมาชิกและคะแนน" />
        <div className="p-[var(--space-4)]">
          <Callout tone="error" title="โหลดรายชื่อสมาชิกไม่สำเร็จ">เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ — ลองใหม่อีกครั้ง</Callout>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-100)] font-[family-name:var(--font-thai)]">
      <Toolbar
        title="สมาชิกและคะแนน"
        count={members.length}
        end={<SearchInput value={query} onChange={setQuery} placeholder="ค้นหาจากชื่อสมาชิก" label="ค้นหาสมาชิก" debounceMs={150} />}
      />

      <div className="flex min-h-0 flex-1">
        {/* ------------------------------------------------ รายชื่อสมาชิก */}
        <nav aria-label="รายชื่อสมาชิก" className="flex w-[340px] shrink-0 flex-col border-r border-[var(--line-200)] bg-[var(--surface-0)]">
          {membersTruncated && (
            <p className="m-0 border-b border-[var(--line-200)] px-[var(--space-3)] py-[var(--space-2)] text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
              แสดง {members.length.toLocaleString('th-TH')} รายชื่อแรก — ค้นหาจากชื่อเพื่อหาคนที่ไม่อยู่ในรายการ
            </p>
          )}
          <ul className="m-0 min-h-0 flex-1 list-none overflow-y-auto p-[var(--space-1)]">
            {shown.map((m) => {
              const active = m.id === selectedId;
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    aria-current={active ? 'true' : undefined}
                    onClick={() => onSelect(m.id)}
                    className={`flex w-full items-center justify-between gap-[var(--space-2)] rounded-[var(--radius-sm)] px-[var(--space-3)] py-[var(--space-2)] text-left outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] ${
                      active ? 'bg-[var(--blue-100)]' : 'hover:bg-[var(--surface-100)]'
                    }`}
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className="bo-truncate text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--ink-900)]">{m.name}</span>
                      <span className="text-[length:var(--text-table-sm)] text-[var(--ink-700)]">{m.tierName ?? 'ยังไม่มีระดับ'}</span>
                    </span>
                    <span className="bo-num shrink-0 text-[length:var(--text-table)] text-[var(--ink-900)]">{pts(m.rewardPoints)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          {shown.length === 0 && (
            <p className="m-0 p-[var(--space-4)] text-center text-[length:var(--text-form)] text-[var(--ink-700)]">
              {members.length === 0 ? 'ยังไม่มีสมาชิก' : 'ไม่พบสมาชิกที่ตรงกับคำค้น'}
            </p>
          )}
        </nav>

        {/* ------------------------------------------------ รายละเอียด */}
        <main className="min-w-0 flex-1 overflow-y-auto p-[var(--space-4)]">
          {!selectedId ? (
            <EmptyState kind="no-data" title="เลือกสมาชิกจากรายการทางซ้าย" description="ดูคะแนนคงเหลือ ที่มาของคะแนนทุกรายการ และปรับคะแนนได้จากที่นี่" />
          ) : detailState === 'error' ? (
            <Callout tone="error" title="โหลดข้อมูลสมาชิกไม่สำเร็จ">ลองเลือกสมาชิกคนนี้อีกครั้ง</Callout>
          ) : !detail || detailState === 'loading' ? (
            <p role="status" className="m-0 text-[length:var(--text-form)] text-[var(--ink-700)]">กำลังโหลดข้อมูลสมาชิก…</p>
          ) : (
            <div className="flex flex-col gap-[var(--space-4)]">
              <header className="flex flex-wrap items-start justify-between gap-[var(--space-3)]">
                <div className="flex flex-col gap-[var(--space-05)]">
                  <h2 className="m-0 text-[length:var(--text-page-title)] font-[var(--weight-semibold)] text-[var(--ink-900)]">{detail.name}</h2>
                  <span className="flex items-center gap-1 text-[length:var(--text-form)] text-[var(--ink-700)]">
                    <Icon name="workspace_premium" size={17} />
                    {detail.tierName ?? 'ยังไม่มีระดับ'}
                  </span>
                </div>
                <button type="button" className={primaryBtnCls} onClick={() => setAdjustOpen(true)}>
                  <Icon name="edit_note" size={17} />
                  ปรับคะแนน
                </button>
              </header>

              <div className="grid grid-cols-2 gap-[var(--space-3)] xl:grid-cols-4">
                {[
                  { label: 'คะแนนแลกของคงเหลือ', value: `${pts(detail.rewardPoints)} คะแนน`, icon: 'redeem', sub: null },
                  { label: 'ยอดใช้จ่ายสะสม', value: `${pts(detail.tierPoints)} บาท`, icon: 'trending_up', sub: 'ใช้คำนวณระดับสมาชิก' },
                  {
                    label: 'ระดับถัดไป',
                    value: detail.nextTier ? detail.nextTier.name : 'ระดับสูงสุดแล้ว',
                    icon: 'workspace_premium',
                    sub: detail.nextTier ? `ใช้จ่ายอีก ${pts(Math.max(0, detail.nextTier.startPoint - detail.tierPoints))} บาท` : null,
                  },
                  {
                    label: 'ใกล้หมดอายุ',
                    value: detail.expiringReward ? `${pts(detail.expiringReward.points)} คะแนน` : 'ไม่มี',
                    icon: 'event_busy',
                    sub: detail.expiringReward ? `หมดอายุ ${fmtDate(detail.expiringReward.at)}` : null,
                  },
                ].map((c) => (
                  <div key={c.label} className="flex flex-col gap-[var(--space-05)] rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-3)]">
                    <span className="flex items-center gap-1 text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
                      <Icon name={c.icon} size={15} />
                      {c.label}
                    </span>
                    <span className="bo-num text-[length:var(--text-section)] font-[var(--weight-semibold)] text-[var(--ink-900)]" style={{ textAlign: 'left' }}>{c.value}</span>
                    {c.sub && <span className="text-[length:var(--text-table-sm)] text-[var(--ink-700)]">{c.sub}</span>}
                  </div>
                ))}
              </div>

              <Tabs
                ariaLabel="ข้อมูลคะแนนของสมาชิก"
                value={tab}
                onChange={(id) => setTab(id as typeof tab)}
                items={[
                  { id: 'history', label: 'ประวัติคะแนน', icon: 'history' },
                  { id: 'adjustments', label: 'รายการปรับคะแนน', icon: 'edit_note', count: adjustments.length },
                ]}
              />

              {tab === 'history' ? (
                <section className="flex flex-col gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-3)]">
                  <p className="m-0 text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                    แสดงเฉพาะคะแนนแลกของ — การปรับยอดใช้จ่ายสะสมดูได้ในแท็บรายการปรับคะแนน
                  </p>

                  <table className="w-full border-collapse text-[length:var(--text-table)]">
                    <thead>
                      <tr className="border-b border-[var(--line-200)] text-left text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
                        <th className="py-[var(--space-2)] pr-[var(--space-2)] font-[var(--weight-medium)]">วันที่</th>
                        <th className="py-[var(--space-2)] pr-[var(--space-2)] font-[var(--weight-medium)]">ที่มา</th>
                        <th className="py-[var(--space-2)] pr-[var(--space-2)] font-[var(--weight-medium)]">รายละเอียด</th>
                        <th className="py-[var(--space-2)] pr-[var(--space-2)] text-right font-[var(--weight-medium)]">คะแนน</th>
                        <th className="py-[var(--space-2)] font-[var(--weight-medium)]">โดย</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h) => {
                        const src = SOURCE_LABEL[h.source] ?? SOURCE_LABEL.OTHER;
                        return (
                          <tr key={`${h.kind}-${h.source}-${h.id}`} className="border-b border-[var(--line-200)] align-top">
                            <td className="py-[var(--space-2)] pr-[var(--space-2)] whitespace-nowrap text-[var(--ink-900)]">{fmtDateTime(h.at)}</td>
                            <td className="py-[var(--space-2)] pr-[var(--space-2)]">
                              <span className="flex items-center gap-1 text-[var(--ink-900)]">
                                <Icon name={src.icon} size={15} className="text-[var(--blue-700)]" />
                                {src.text}
                              </span>
                            </td>
                            <td className="py-[var(--space-2)] pr-[var(--space-2)] text-[var(--ink-900)]">
                              {h.referenceText && <span className="block">{h.referenceText}</span>}
                              {h.adjustment && (
                                <span className="block text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                                  {REASON_LABEL[h.adjustment.reasonCode]} — {h.adjustment.reason}
                                </span>
                              )}
                              {h.cancelled && (
                                <span className="mt-[2px] inline-flex items-center gap-1 text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
                                  <Icon name="block" size={14} />
                                  ถูกย้อนรายการแล้ว
                                </span>
                              )}
                            </td>
                            <td
                              className="bo-num py-[var(--space-2)] pr-[var(--space-2)] font-[var(--weight-semibold)]"
                              style={{ color: amountColor(h.amount), textDecoration: h.cancelled ? 'line-through' : undefined }}
                            >
                              {signed(h.amount)}
                            </td>
                            <td className="py-[var(--space-2)] text-[var(--ink-700)]">{h.byName ?? '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {history.length === 0 && !historyLoading && (
                    <p className="m-0 py-[var(--space-4)] text-center text-[length:var(--text-form)] text-[var(--ink-700)]">ยังไม่มีประวัติคะแนน</p>
                  )}
                  {historyLoading && <p role="status" className="m-0 text-[length:var(--text-form)] text-[var(--ink-700)]">กำลังโหลด…</p>}
                  {historyHasMore && !historyLoading && (
                    <button type="button" onClick={onLoadMoreHistory} className={`${ghostBtnCls} self-center`}>
                      <Icon name="expand_more" size={17} />
                      แสดงเพิ่ม
                    </button>
                  )}
                </section>
              ) : (
                <section className="flex flex-col gap-[var(--space-2)]">
                  {adjustments.length === 0 && (
                    <p className="m-0 rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-4)] text-center text-[length:var(--text-form)] text-[var(--ink-700)]">
                      ยังไม่เคยปรับคะแนนของสมาชิกคนนี้
                    </p>
                  )}
                  {adjustments.map((a) => {
                    const cancelled = a.status === 'CANCELLED';
                    return (
                      <article key={a.id} className="flex flex-col gap-[var(--space-1)] rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-3)]">
                        <div className="flex flex-wrap items-center gap-[var(--space-2)]">
                          {a.rewardAmount !== 0 && (
                            <span className="bo-num font-[var(--weight-semibold)]" style={{ color: amountColor(a.rewardAmount), textDecoration: cancelled ? 'line-through' : undefined }}>
                              {KIND_LABEL.REWARD} {signed(a.rewardAmount)} คะแนน
                            </span>
                          )}
                          {a.tierAmount !== 0 && (
                            <span className="bo-num font-[var(--weight-semibold)]" style={{ color: amountColor(a.tierAmount), textDecoration: cancelled ? 'line-through' : undefined }}>
                              {KIND_LABEL.TIER} {signed(a.tierAmount)} บาท
                            </span>
                          )}
                          <span
                            className="inline-flex items-center gap-1 rounded-[var(--radius-pill)] px-2 py-[var(--space-05)] text-[length:var(--text-table-sm)] font-[var(--weight-medium)]"
                            style={cancelled
                              ? { background: 'var(--surface-100)', color: 'var(--ink-700)' }
                              : { background: 'var(--success-bg)', color: 'var(--success-text)' }}
                          >
                            <Icon name={cancelled ? 'block' : 'check_circle'} size={14} filled />
                            {cancelled ? 'ยกเลิกแล้ว' : 'มีผลแล้ว'}
                          </span>
                          <span className="flex-1" />
                          {!cancelled && (
                            <button type="button" className={ghostBtnCls} style={{ color: 'var(--error-text)' }} onClick={() => setCancelTarget(a)}>
                              <Icon name="cancel" size={16} />
                              ยกเลิกรายการนี้
                            </button>
                          )}
                        </div>
                        <p className="m-0 text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)]">
                          <strong>{REASON_LABEL[a.reasonCode]}</strong> — {a.reason}
                        </p>
                        <p className="m-0 text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                          ลูกค้าเห็น: "{a.customerNote || DEFAULT_CUSTOMER_NOTE}"
                          {a.reference && <> · อ้างอิง {a.reference}</>}
                          {a.expiresAt && <> · ของที่เพิ่มหมดอายุ {fmtDate(a.expiresAt)}</>}
                        </p>
                        <p className="m-0 text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
                          โดย {a.byName} · {fmtDateTime(a.at)}
                        </p>
                        {cancelled && (
                          <p className="m-0 text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                            ยกเลิกโดย {a.cancelledByName ?? '—'}{a.cancelledAt ? ` · ${fmtDateTime(a.cancelledAt)}` : ''} — {a.cancelReason}
                          </p>
                        )}
                      </article>
                    );
                  })}
                </section>
              )}
            </div>
          )}
        </main>
      </div>

      {adjustOpen && detail && (
        <AdjustDialog member={detail} tiers={tiers} onClose={() => setAdjustOpen(false)} onSubmit={submitAdjust} />
      )}

      <ConfirmDialog
        open={Boolean(cancelTarget)}
        tone="danger"
        title="ยกเลิกการปรับคะแนน"
        description={cancelTarget ? (
          <p className="m-0">
            คะแนนที่ปรับไว้จะถูกย้อนกลับ ({[
              cancelTarget.rewardAmount !== 0 ? `${KIND_LABEL.REWARD} ${signed(-cancelTarget.rewardAmount)} ${KIND_UNIT.REWARD}` : null,
              cancelTarget.tierAmount !== 0 ? `${KIND_LABEL.TIER} ${signed(-cancelTarget.tierAmount)} ${KIND_UNIT.TIER}` : null,
            ].filter(Boolean).join(' · ')}) — ถ้าลูกค้าใช้คะแนนที่เพิ่มไปแล้ว จะยกเลิกไม่ได้
          </p>
        ) : ''}
        confirmLabel="ยืนยันยกเลิก"
        requireReason
        minReasonLength={REASON_MIN}
        reasonLabel="เหตุผลการยกเลิก"
        busy={cancelBusy}
        onConfirm={(reason) => { void confirmCancel(reason); }}
        onCancel={() => setCancelTarget(null)}
      />
    </div>
  );
};

export default MemberScreen;

/* ============================================================================
   รองรับ:
   FR-ADM-04   ปรับคะแนนแบบ Manual โดยต้องระบุเหตุผล — เหตุผลเก็บกับรายการปรับ (ไม่ใช่ใน AuditLog กลาง)
   FR-ADM-37   ใคร · เมื่อไร · เท่าไร · เพราะอะไร ของทุกการปรับและการยกเลิก แสดงในแท็บรายการปรับคะแนน
   FR-PT-12    ประวัติคะแนนพร้อมที่มาและวันหมดอายุของก้อน (มุมมองเจ้าหน้าที่)

   ประเด็นค้าง (⛔):
   — ค้นหาสมาชิกกรองจากรายชื่อที่โหลดมาเท่านั้น backend ยังไม่มีการค้นหาที่ server
   — ทุกคนที่เป็นสมาชิกของหน่วยงานปรับคะแนนได้ — backend ยังไม่มีสิทธิ์แยก
   — ปรับเพิ่มคะแนนชนิดหนึ่งพร้อมหักอีกชนิดในรายการเดียวไม่ได้จากหน้านี้ (backend รับได้)
   ============================================================================ */
